import { useCallback, useEffect, useRef, useState } from 'react';
import QrScanner from 'qr-scanner';
import Quagga from '@ericblade/quagga2';

/**
 * Kamera a dekódování kódů.
 *
 * Proti původnímu `scanner.js` se mění tři věci:
 *  1. knihovny jsou z npm, ne z CDN (start nezávisí na síti),
 *  2. lifecycle je svázaný s komponentou včetně cleanupu (StrictMode double-invoke),
 *  3. `lockRef` zabrání dvojímu vystřelení callbacku, když QR i Quagga
 *     dekódují tentýž kód ve stejném okamžiku.
 */
export type ScannerStatus = 'idle' | 'starting' | 'running' | 'error';

export function useScanner(onCode: (code: string) => void) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const qrRef = useRef<QrScanner | null>(null);
  const quaggaRef = useRef(false);
  const lockRef = useRef(false);
  const onCodeRef = useRef(onCode);
  const [status, setStatus] = useState<ScannerStatus>('idle');

  useEffect(() => {
    onCodeRef.current = onCode;
  }, [onCode]);

  const emit = useCallback((code: string) => {
    if (lockRef.current || !code) return;
    lockRef.current = true;
    onCodeRef.current(code);
  }, []);

  const stop = useCallback(() => {
    qrRef.current?.stop();
    if (quaggaRef.current) {
      try {
        Quagga.stop();
      } catch {
        /* Quagga hází, když ještě neběžel */
      }
      quaggaRef.current = false;
    }
    setStatus('idle');
  }, []);

  const start = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;
    lockRef.current = false;
    setStatus('starting');

    try {
      if (!qrRef.current) {
        qrRef.current = new QrScanner(
          video,
          (result) => emit(typeof result === 'string' ? result : result.data),
          {
            preferredCamera: 'environment',
            highlightScanRegion: true,
            highlightCodeOutline: true,
            maxScansPerSecond: 10,
            returnDetailedScanResult: true,
            onDecodeError: () => {
              /* střílí neustále, ticho je záměr */
            },
          },
        );
      }
      await qrRef.current.start();

      // 1D čárové kódy – Quagga jede paralelně nad stejným videem.
      if (!quaggaRef.current) {
        await new Promise<void>((resolve) => {
          Quagga.init(
            {
              inputStream: {
                type: 'LiveStream',
                target: video,
                constraints: { facingMode: 'environment' },
              },
              decoder: {
                readers: [
                  'code_128_reader', 'ean_reader', 'ean_8_reader', 'code_39_reader',
                  'codabar_reader', 'upc_reader', 'upc_e_reader', 'i2of5_reader',
                ],
              },
              locate: true,
              locator: { halfSample: true, patchSize: 'medium' },
              numOfWorkers: 2,
              frequency: 10,
            },
            (err: unknown) => {
              if (err) {
                console.warn('[scanner] Quagga se nepodařilo spustit:', err);
                resolve();
                return;
              }
              Quagga.onDetected((r) => {
                const code = r?.codeResult?.code;
                if (code) emit(code);
              });
              Quagga.start();
              quaggaRef.current = true;
              resolve();
            },
          );
        });
      }

      setStatus('running');
    } catch (err) {
      console.error('[scanner] kamera:', err);
      setStatus('error');
    }
  }, [emit]);

  useEffect(() => {
    return () => {
      stop();
      qrRef.current?.destroy();
      qrRef.current = null;
    };
  }, [stop]);

  return { videoRef, status, start, stop };
}
